/**
 * Utility functions for react-big-calendar view-based date range detection
 * Automatically detects the visible date range based on calendar view and current date
 */

/**
 * Get the start and end dates for the current calendar view
 * @param {string} view - react-big-calendar view type ('month', 'week', 'day', 'agenda', etc.)
 * @param {Date} currentDate - Current date from FullCalendar
 * @returns {Object} - { startDate, endDate }
 */
export const getViewDateRange = (view, currentDate) => {
  if (!currentDate) {
    currentDate = new Date();
  }

  const date = new Date(currentDate);
  
  switch (view) {
    case 'month':
      return getMonthRange(date);

    case 'week':
    case 'agenda':
      return getWeekRange(date);

    case 'day':
      return getDayRange(date);

    default:
      // Default to month view
      return getMonthRange(date);
  }
};

/**
 * Get month range with extended period (includes adjacent month days)
 * For month view, includes last 15 days of previous month and first 15 days of next month
 * @param {Date} date 
 * @returns {Object}
 */
const getMonthRange = (date) => {
  const currentMonth = date.getMonth();
  const currentYear = date.getFullYear();
  
  // Calcular 15 dias antes do início do mês atual
  const monthStartDate = new Date(currentYear, currentMonth, 1);
  const startDate = new Date(monthStartDate);
  startDate.setDate(startDate.getDate() - 15); // 15 dias antes
  startDate.setHours(0, 0, 0, 0);
  
  // Calcular 15 dias depois do fim do mês atual
  const monthEndDate = new Date(currentYear, currentMonth + 1, 0); // Último dia do mês atual
  const endDate = new Date(monthEndDate);
  endDate.setDate(endDate.getDate() + 15); // 15 dias depois
  endDate.setHours(23, 59, 59, 999);
  
  return {
    startDate,
    endDate,
    description: `${getMonthName(currentMonth)} ${currentYear}`,
    extendedDescription: `${getMonthName(currentMonth)} ${currentYear} (+ 15 dias adjacentes)`,
    isExtended: true,
    coreMonth: {
      start: monthStartDate,
      end: monthEndDate,
      month: currentMonth,
      year: currentYear
    }
  };
};

/**
 * Get week range (Sunday to Saturday or Monday to Sunday based on locale)
 * @param {Date} date 
 * @returns {Object}
 */
const getWeekRange = (date) => {
  const currentDate = new Date(date);
  const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  
  // Calculate start of week (Sunday)
  const startDate = new Date(currentDate);
  startDate.setDate(currentDate.getDate() - dayOfWeek);
  startDate.setHours(0, 0, 0, 0);
  
  // Calculate end of week (Saturday)
  const endDate = new Date(startDate);
  endDate.setDate(startDate.getDate() + 6);
  endDate.setHours(23, 59, 59, 999);
  
  return {
    startDate,
    endDate,
    description: `${formatDateShort(startDate)} - ${formatDateShort(endDate)}`
  };
};

/**
 * Get day range (00:00 to 23:59 of the specific day)
 * @param {Date} date 
 * @returns {Object}
 */
const getDayRange = (date) => {
  const startDate = new Date(date);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(date);
  endDate.setHours(23, 59, 59, 999);
  
  return {
    startDate,
    endDate,
    description: formatDateLong(date)
  };
};

/**
 * Get year range (January 1st to December 31st)
 * @param {Date} date 
 * @returns {Object}
 */
const getYearRange = (date) => {
  const startDate = new Date(date.getFullYear(), 0, 1);
  const endDate = new Date(date.getFullYear(), 11, 31, 23, 59, 59, 999);
  
  return {
    startDate,
    endDate,
    description: `Ano ${date.getFullYear()}`
  };
};

/**
 * Helper function to get month name in Portuguese
 * @param {number} monthIndex 
 * @returns {string}
 */
const getMonthName = (monthIndex) => {
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  return months[monthIndex];
};

/**
 * Format date for short display (dd/MM)
 * @param {Date} date 
 * @returns {string}
 */
const formatDateShort = (date) => {
  return date.toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: '2-digit' 
  });
};

/**
 * Format date for long display (dd/MM/yyyy)
 * @param {Date} date 
 * @returns {string}
 */
const formatDateLong = (date) => {
  return date.toLocaleDateString('pt-BR', { 
    day: '2-digit', 
    month: '2-digit', 
    year: 'numeric'
  });
};

/**
 * Check if view-based filtering should override manual date filters
 * @param {Object} dateFilters - Manual date filters from user
 * @param {string} view - Current calendar view
 * @returns {boolean}
 */
export const shouldUseViewBasedFiltering = (dateFilters, view) => {
  // If user has manually set date filters, respect them
  if (dateFilters.startDate || dateFilters.endDate) {
    return false;
  }
  
  // Otherwise, use view-based filtering for all views
  return true;
};

/**
 * Get effective date range (either manual filters or view-based)
 * @param {Object} dateFilters - Manual date filters
 * @param {string} view - Current calendar view
 * @param {Date} currentDate - Current react-big-calendar date
 * @returns {Object}
 */
export const getEffectiveDateRange = (dateFilters, view, currentDate) => {
  if (shouldUseViewBasedFiltering(dateFilters, view)) {
    return getViewDateRange(view, currentDate);
  }
  
  return {
    startDate: dateFilters.startDate,
    endDate: dateFilters.endDate,
    description: 'Filtro personalizado'
  };
};

/**
 * Format date range for API (YYYY-MM-DD format)
 * @param {Object} dateRange 
 * @returns {Object}
 */
export const formatDateRangeForAPI = (dateRange) => {
  const result = {};
  
  if (dateRange.startDate) {
    result.startDate = dateRange.startDate.toISOString().split('T')[0];
  }
  
  if (dateRange.endDate) {
    result.endDate = dateRange.endDate.toISOString().split('T')[0];
  }
  
  return result;
};

/**
 * Get only the current month range (without extension)
 * @param {Date} date 
 * @returns {Object}
 */
export const getCurrentMonthOnlyRange = (date) => {
  const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(date.getFullYear(), date.getMonth() + 1, 0);
  endDate.setHours(23, 59, 59, 999);
  
  return {
    startDate,
    endDate,
    description: `${getMonthName(date.getMonth())} ${date.getFullYear()} (apenas mês atual)`,
    isExtended: false
  };
};

/**
 * Debug function to log current view and date range with extended info
 * @param {string} view 
 * @param {Date} currentDate 
 * @param {Object} dateRange 
 */
export const debugViewDateRange = (view, currentDate, dateRange) => {
  if (process.env.NODE_ENV === 'development') {
    const debugInfo = {
      view,
      currentDate: currentDate?.toISOString(),
      dateRange: {
        startDate: dateRange.startDate?.toISOString(),
        endDate: dateRange.endDate?.toISOString(),
        description: dateRange.description
      }
    };

    // Para visão mensal, adicionar informações extras sobre a extensão
    if (view === 'month' && dateRange.isExtended) {
      debugInfo.monthDetails = {
        coreMonth: {
          start: dateRange.coreMonth?.start?.toISOString(),
          end: dateRange.coreMonth?.end?.toISOString(),
          month: dateRange.coreMonth?.month,
          year: dateRange.coreMonth?.year
        },
        extendedDescription: dateRange.extendedDescription,
        totalDays: Math.ceil((dateRange.endDate - dateRange.startDate) / (1000 * 60 * 60 * 24))
      };
    }

    console.log('📅 Calendar View Debug:', debugInfo);
  }
};

/**
 * Test function to demonstrate extended month range logic
 * @param {Date} testDate - Date to test (optional, defaults to current date)
 */
export const testExtendedMonthRange = (testDate = new Date()) => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🧪 Testando lógica do período estendido para visão mensal:');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    
    const monthRange = getViewDateRange('month', testDate);
    
    console.log('📅 Data de teste:', testDate.toLocaleDateString('pt-BR'));
    console.log('🗓️ Mês principal:', `${getMonthName(testDate.getMonth())} ${testDate.getFullYear()}`);
    console.log('');
    console.log('📍 Período estendido:');
    console.log('  ├── Início:', monthRange.startDate.toLocaleDateString('pt-BR'), monthRange.startDate.toLocaleTimeString('pt-BR'));
    console.log('  └── Fim:', monthRange.endDate.toLocaleDateString('pt-BR'), monthRange.endDate.toLocaleTimeString('pt-BR'));
    console.log('');
    console.log('📊 Detalhes:');
    console.log('  ├── Mês anterior (15 dias):', monthRange.coreMonth.start.toLocaleDateString('pt-BR'));
    console.log('  ├── Mês atual (completo):', `${monthRange.coreMonth.start.toLocaleDateString('pt-BR')} - ${monthRange.coreMonth.end.toLocaleDateString('pt-BR')}`);
    console.log('  └── Próximo mês (15 dias):', monthRange.coreMonth.end.toLocaleDateString('pt-BR'));
    console.log('');
    console.log('📈 Total de dias no período:', Math.ceil((monthRange.endDate - monthRange.startDate) / (1000 * 60 * 60 * 24)));
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  }
};